import React, { useMemo, useRef, useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import PageInstructions from "@/components/PageInstructions";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  gerarDesdobramentoCompleto,
  calcularCusto,
  isCentenaValida,
  type DesdobramentoCompleto,
} from "@/utils/centenaMatadora";
import {
  Share2, Download, Save, Trash2, RefreshCw, CheckCircle2, XCircle,
  Clock, MessageCircle, Facebook, Image as ImageIcon, Send, AlertTriangle,
} from "lucide-react";
import html2canvas from "html2canvas";

interface JogoSalvo {
  id: string;
  centena_base: string;
  desdobramento: DesdobramentoCompleto;
  posicoes: number[];
  custo: number;
  conferido: boolean;
  premiado: boolean;
  created_at: string;
  expires_at?: string;
}

const POSICOES_DISPONIVEIS = [1, 2, 3, 4, 5];
const SHARE_URL = "https://aipalpiteguru.mentepoderosa2030.com/metodos?method=centena-matadora";

const ColunaDesdobramento: React.FC<{
  titulo: string;
  cor: string;
  numeros: string[];
}> = ({ titulo, cor, numeros }) => (
  <Card className="border-2 border-border">
    <CardHeader className={`${cor} text-white py-2 px-3 rounded-t-lg`}>
      <div className="flex items-center justify-between">
        <CardTitle className="text-sm font-bold">{titulo}</CardTitle>
        <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full bg-white text-black border border-black/30 text-xs font-extrabold shadow">
          {numeros.length}
        </span>
      </div>
    </CardHeader>
    <CardContent className="p-2 bg-card">
      <div className="grid grid-cols-2 gap-1 max-h-80 overflow-y-auto">
        {numeros.map((n, i) => (
          <div
            key={i}
            className="bg-muted text-foreground text-center py-1 px-2 rounded text-sm font-mono font-bold"
          >
            {i + 1}. {n}
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const diasParaExpirar = (expiresAt?: string): number => {
  if (!expiresAt) return 7;
  const ms = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
};

const CentenaMatadora: React.FC = () => {
  const { user } = useAuth();
  const [base, setBase] = useState("");
  const [posicoes, setPosicoes] = useState<number[]>([1, 2, 3, 4, 5]);
  const [valorAposta, setValorAposta] = useState(1);
  const [resultado, setResultado] = useState<DesdobramentoCompleto | null>(null);
  const [jogosSalvos, setJogosSalvos] = useState<JogoSalvo[]>([]);
  const [conferindo, setConferindo] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  const totalCentenas = useMemo(
    () =>
      resultado
        ? resultado.seca.length + resultado.curto.length + resultado.medio.length + resultado.matador.length
        : 0,
    [resultado]
  );

  const custo = useMemo(
    () => calcularCusto(totalCentenas, posicoes.length, valorAposta),
    [totalCentenas, posicoes, valorAposta]
  );

  const carregarJogos = async () => {
    if (!user) return;
    // Limpeza on-demand de expirados (>7 dias)
    try {
      await supabase.rpc("cleanup_expired_centena_matadora" as any);
    } catch { /* silencioso */ }

    const { data, error } = await supabase
      .from("centena_matadora_jogos")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(20);
    if (!error && data) setJogosSalvos(data as unknown as JogoSalvo[]);
  };

  useEffect(() => {
    carregarJogos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleGerar = () => {
    if (!isCentenaValida(base)) {
      toast.error("Informe uma centena válida (3 dígitos, ex: 057)");
      return;
    }
    if (posicoes.length === 0) {
      toast.error("Selecione ao menos 1 posição de prêmio");
      return;
    }
    setResultado(gerarDesdobramentoCompleto(base));
  };

  const togglePos = (p: number) => {
    setPosicoes((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p].sort()
    );
  };

  const handleSalvar = async () => {
    if (!user || !resultado) return;
    const { error } = await supabase.from("centena_matadora_jogos").insert({
      user_id: user.id,
      centena_base: base,
      desdobramento: resultado as any,
      posicoes,
      valor_aposta: valorAposta,
      custo,
    });
    if (error) {
      toast.error("Erro ao salvar jogo");
      return;
    }
    toast.success("Jogo salvo! Disponível por 7 dias.");
    carregarJogos();
  };

  const handleExcluir = async (id: string) => {
    if (!window.confirm("Excluir este jogo salvo? Esta ação não pode ser desfeita.")) return;
    const { error } = await supabase.from("centena_matadora_jogos").delete().eq("id", id);
    if (error) {
      toast.error("Erro ao excluir");
      return;
    }
    toast.success("Jogo excluído");
    carregarJogos();
  };

  const handleConferir = async () => {
    if (!user || jogosSalvos.length === 0) return;
    setConferindo(true);
    try {
      const { data: resultados } = await supabase
        .from("jogo_bicho_resultados")
        .select("*")
        .is("user_id", null)
        .order("data", { ascending: false })
        .limit(50);

      const todasCentenas: string[] = [];
      (resultados || []).forEach((r: any) => {
        ["ppt", "ptm", "pt", "ptv", "ptn", "coruja"].forEach((t) => {
          for (let i = 1; i <= 5; i++) {
            const m = r[`${t}_${i}`];
            if (m) todasCentenas.push(String(m).slice(-3));
          }
        });
      });

      for (const j of jogosSalvos) {
        if (j.conferido) continue;
        const todas = [
          ...j.desdobramento.seca,
          ...j.desdobramento.curto,
          ...j.desdobramento.medio,
          ...j.desdobramento.matador,
        ];
        const acertou = todas.some((c) => todasCentenas.includes(c));
        await supabase
          .from("centena_matadora_jogos")
          .update({ conferido: true, premiado: acertou })
          .eq("id", j.id);
      }
      toast.success("Conferência concluída!");
      carregarJogos();
    } catch {
      toast.error("Erro ao conferir");
    } finally {
      setConferindo(false);
    }
  };

  const buildShareText = () => {
    if (!resultado) return "";
    return `🎯 CENTENA MATADORA — Base ${base} 🎯
🔥 Seca: ${resultado.seca.join(" • ")}
⚡ Curto: ${resultado.curto.join(" • ")}
💎 Médio: ${resultado.medio.join(" • ")}
🚀 Matador: ${resultado.matador.join(" • ")}
Posições: ${posicoes.join("-")}º | Custo: R$ ${custo.toFixed(2)}
Gerado pelo IA Palpite Guru — ${SHARE_URL}`;
  };

  const exportCanvas = async (): Promise<HTMLCanvasElement | null> => {
    if (!exportRef.current) return null;
    return await html2canvas(exportRef.current, { backgroundColor: "#ffffff", scale: 2 });
  };

  const handleShareWhatsApp = () => {
    const texto = buildShareText();
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, "_blank");
  };

  const handleShareFacebook = () => {
    const texto = buildShareText();
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}&quote=${encodeURIComponent(texto)}`,
      "_blank"
    );
  };

  const handleDownloadImage = async () => {
    try {
      const canvas = await exportCanvas();
      if (!canvas) return;
      const link = document.createElement("a");
      link.download = `centena-matadora-${base}-ia-palpite-guru.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Imagem gerada!");
    } catch {
      toast.error("Erro ao gerar imagem");
    }
  };

  const handleShareStatus = async () => {
    const texto = buildShareText();
    try {
      const canvas = await exportCanvas();
      if (canvas && (navigator as any).canShare) {
        const blob: Blob = await new Promise((res) =>
          canvas.toBlob((b) => res(b as Blob), "image/png")
        );
        const file = new File([blob], `centena-matadora-${base}.png`, { type: "image/png" });
        if ((navigator as any).canShare({ files: [file] })) {
          await (navigator as any).share({
            title: "Centena Matadora",
            text: texto,
            files: [file],
          });
          return;
        }
      }
      if (navigator.share) {
        await navigator.share({ title: "Centena Matadora", text: texto, url: SHARE_URL });
        return;
      }
      await navigator.clipboard.writeText(texto);
      toast.success("Texto copiado! Cole no seu Status.");
      handleDownloadImage();
    } catch {
      /* cancelado */
    }
  };

  return (
    <div className="w-full space-y-6">
      <PageInstructions
        title="📖 Como usar a Centena Matadora"
        steps={[
          "Digite uma centena base de 3 dígitos (ex: 057)",
          "Escolha as posições de prêmio (1º ao 5º)",
          "Defina o valor por aposta para calcular o custo total",
          "Clique em Gerar para ver os 4 níveis de desdobramento",
          "Salve o jogo, compartilhe (WhatsApp/Facebook/Status) ou exporte como imagem",
          "Use 'Conferir' para verificar acertos contra os últimos resultados",
        ]}
      />

      <div className="text-center">
        <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 bg-clip-text text-transparent">
          🎯 CENTENA MATADORA
        </h1>
        <p className="text-muted-foreground mt-2">
          Desdobramento progressivo com 4 níveis de cobertura
        </p>
      </div>

      <Card className="border-2 border-primary/30">
        <CardContent className="p-4 md:p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="base">Centena base (000-999)</Label>
              <Input
                id="base"
                value={base}
                onChange={(e) => setBase(e.target.value.replace(/\D/g, "").slice(0, 3))}
                placeholder="Ex: 057"
                className="text-2xl font-mono font-bold text-center"
                maxLength={3}
              />
            </div>
            <div>
              <Label>Valor por aposta (R$)</Label>
              <Input
                type="number"
                min={0.5}
                step={0.5}
                value={valorAposta}
                onChange={(e) => setValorAposta(parseFloat(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label>Posições de prêmio</Label>
              <div className="flex gap-2 flex-wrap mt-2">
                {POSICOES_DISPONIVEIS.map((p) => (
                  <label key={p} className="flex items-center gap-1 cursor-pointer">
                    <Checkbox
                      checked={posicoes.includes(p)}
                      onCheckedChange={() => togglePos(p)}
                    />
                    <span className="text-sm font-semibold">{p}º</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <Button
            onClick={handleGerar}
            className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:opacity-90 text-white font-bold py-5 text-lg"
          >
            🎰 GERAR CENTENA MATADORA
          </Button>
        </CardContent>
      </Card>

      {resultado && (
        <>
          <div ref={exportRef} className="bg-background p-4 rounded-lg space-y-4">
            <div className="text-center">
              <Badge variant="outline" className="text-base font-bold border-2 border-primary px-4 py-1">
                Base: {base} • Posições: {posicoes.join("-")}º • Custo: R$ {custo.toFixed(2)}
              </Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <ColunaDesdobramento
                titulo="🔥 SECA (4)"
                cor="bg-gradient-to-r from-red-600 to-red-700"
                numeros={resultado.seca}
              />
              <ColunaDesdobramento
                titulo="⚡ CURTO (10)"
                cor="bg-gradient-to-r from-orange-500 to-orange-600"
                numeros={resultado.curto}
              />
              <ColunaDesdobramento
                titulo="💎 MÉDIO (20)"
                cor="bg-gradient-to-r from-yellow-500 to-amber-600"
                numeros={resultado.medio}
              />
              <ColunaDesdobramento
                titulo="🚀 MATADOR (30)"
                cor="bg-gradient-to-r from-emerald-600 to-green-700"
                numeros={resultado.matador}
              />
            </div>
            <p className="text-center text-xs text-muted-foreground italic">
              Gerado por IA Palpite Guru
            </p>
          </div>

          <div className="flex flex-wrap gap-2 justify-center">
            {user && (
              <Button onClick={handleSalvar} variant="default">
                <Save className="w-4 h-4 mr-2" /> Salvar Jogo
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary">
                  <Share2 className="w-4 h-4 mr-2" /> Compartilhar
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-56">
                <DropdownMenuItem onClick={handleShareWhatsApp}>
                  <MessageCircle className="w-4 h-4 mr-2 text-green-600" /> WhatsApp
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleShareFacebook}>
                  <Facebook className="w-4 h-4 mr-2 text-blue-600" /> Facebook
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDownloadImage}>
                  <ImageIcon className="w-4 h-4 mr-2 text-purple-600" /> Baixar Imagem
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleShareStatus}>
                  <Send className="w-4 h-4 mr-2 text-orange-600" /> Status / Story
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button onClick={handleDownloadImage} variant="outline">
              <Download className="w-4 h-4 mr-2" /> Baixar Imagem
            </Button>
          </div>
        </>
      )}

      {user && (
        <Alert className="border-amber-400 bg-amber-50 dark:bg-amber-950/30">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-sm">
            <strong>Atenção:</strong> Seus jogos salvos ficam disponíveis por <strong>7 dias</strong> e
            depois são apagados automaticamente. Para excluir antes, use o ícone da lixeira 🗑️.
            Eles aparecem aqui mesmo, no card abaixo, para conferência futura.
          </AlertDescription>
        </Alert>
      )}

      {user && jogosSalvos.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>📋 Meus Jogos Salvos ({jogosSalvos.length})</CardTitle>
            <Button size="sm" onClick={handleConferir} disabled={conferindo}>
              <RefreshCw className={`w-4 h-4 mr-2 ${conferindo ? "animate-spin" : ""}`} />
              Conferir
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {jogosSalvos.map((j) => {
              const dias = diasParaExpirar(j.expires_at);
              return (
                <div
                  key={j.id}
                  className="flex items-center justify-between p-3 bg-muted rounded-lg gap-2 flex-wrap"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold text-lg">{j.centena_base}</span>
                    <Badge variant="outline">{j.posicoes.join("-")}º</Badge>
                    <span className="text-sm text-muted-foreground">
                      R$ {Number(j.custo).toFixed(2)}
                    </span>
                    <Badge
                      variant="outline"
                      className={`text-xs ${
                        dias <= 1 ? "border-red-500 text-red-600" : "border-amber-500 text-amber-700"
                      }`}
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      {dias === 0 ? "Expira hoje" : `Expira em ${dias}d`}
                    </Badge>
                    {j.conferido &&
                      (j.premiado ? (
                        <Badge className="bg-green-600">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Acertou
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <XCircle className="w-3 h-3 mr-1" /> Não saiu
                        </Badge>
                      ))}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleExcluir(j.id)}
                    aria-label="Excluir jogo"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CentenaMatadora;
